import { Box, ColorOptions, Image, MaterialTextInput, TextInputNew } from '@jfs/ui-new'
import { AmexIcon } from '@jfs/ui-new/src/assets/images/svgs/Amex'
import { CardIcon } from '@jfs/ui-new/src/assets/images/svgs/Card'
import { InvalidCardIcon } from '@jfs/ui-new/src/assets/images/svgs/InvalidCard'
import { MaestroIcon } from '@jfs/ui-new/src/assets/images/svgs/Maestro'
import { MasterCardIcon } from '@jfs/ui-new/src/assets/images/svgs/MasterCard'
import { RupayIcon } from '@jfs/ui-new/src/assets/images/svgs/Rupay'
import { VisaIcon } from '@jfs/ui-new/src/assets/images/svgs/Visa'
import {
  checkValidCard,
  getCardValidatonError,
  isValidCardNumber as checkValidCardNumber,
} from '@jfs/ui-new/src/utils/validations'
import { isEmpty } from '@jfs/utils'
import { useEffect, useState } from 'react'
import { TextInput } from 'react-native'
import {
  cardFormatSpacersLength,
  CardVendors,
  convertHexToRGBA,
  formatCardNumber,
  getCardVendorAndImage,
  isSavedCardVisible,
  maxCardLength,
  maxCVVLength,
} from '../../utils'
import { creditCardStyles } from './styles'

const renderCardIcon = (cardVendor: string | undefined, cardImage: string | undefined) => {
  switch (cardVendor) {
    case 'amex':
      return <AmexIcon width={32} height={32} />
    case 'maestro':
      return <MaestroIcon width={30} height={30} />
    case 'mastercard':
      return <MasterCardIcon width={30} height={30} />
    case 'visa':
      return <VisaIcon width={30} height={30} />
    case 'rupay':
      return <RupayIcon width={30} height={30} />
    case 'none':
      return <CardIcon width={30} height={30} color={'#d1e0ff'} />
    default:
      return <Image width={30} height={30} source={cardImage} resizeMode="center" />
  }
}

interface CardNumberInputProps {
  value?: string
  innerRef?: React.RefObject<TextInput>
  errMsg?: string
  nextRef?: React.RefObject<TextInput>
  onCardNumberBlur?: (cardVendor: CardVendors, cardNumber: string) => void
  isValidCardNumber: (value: boolean) => void
  isValid?: boolean
  setMaxCvvLength: (value: number) => void
  cardNumber: string
  setCardNumber: (value: string) => void
  theme?: any
  errorColor?: ColorOptions
  onSaveCardVisible?: (isVisible: boolean) => void
  setCurCardVendor?: (cardVendor: string) => void
  isMaterialInput?: boolean
  cardNumFooter?: React.ReactNode
  placeholder?: string
}

const CardNumberInput = ({
  innerRef,
  nextRef,
  onCardNumberBlur,
  isValidCardNumber,
  setMaxCvvLength,
  cardNumber,
  setCardNumber,
  errMsg,
  theme,
  errorColor,
  onSaveCardVisible,
  setCurCardVendor,
  isMaterialInput = true,
  cardNumFooter,
  placeholder,
}: CardNumberInputProps) => {
  const [errorMsg, setErrorMsg] = useState('')
  const [digitLength, setDigitLength] = useState(16)
  const [maxLength, setMaxLength] = useState(16)
  const [cardImage, setCardImage] = useState<string | undefined>()
  const [cardVendor, setCardVendor] = useState<CardVendors>()
  const [checkedCardNumber, setCheckedCardNumber] = useState(false)
  const [cursor, setCursor] = useState(false)
  const [cursorPosition, setCursorPostion] = useState(0)
  const [previousCursorPosition, setPreviousCursorPosition] = useState(0)
  const [previousCardNumber, setPreviousCardNumber] = useState('')

  const styles = creditCardStyles(theme)
  // To calculate maxLength of Card Number and CVV
  useEffect(() => {
    if (cardVendor) {
      const cardLength = maxCardLength[cardVendor]
      const cardLengthWithSpaces = cardLength + cardFormatSpacersLength[cardVendor]
      setDigitLength(cardLength)
      setMaxLength(cardLengthWithSpaces)
      setMaxCvvLength(maxCVVLength[cardVendor])
    }
  }, [cardVendor, setMaxCvvLength])

  useEffect(() => {
    if (cardNumber !== undefined) {
      if (cardNumber.length < 3) {
        setCardImage(undefined)
        onSaveCardVisible && onSaveCardVisible(false)
        setCurCardVendor && setCurCardVendor('')
      } else if (cardNumber.length > 2) {
        const [curCardVendor, curCardImage] = getCardVendorAndImage(cardNumber.replace(/[^0-9]/g, ''))
        setCardImage(curCardImage)
        onSaveCardVisible && onSaveCardVisible(isSavedCardVisible(curCardVendor))
        setCurCardVendor && setCurCardVendor(curCardVendor)
        setCardVendor(curCardVendor as CardVendors)
      }
    }
  }, [cardNumber, cardVendor, onSaveCardVisible, setCurCardVendor])

  const onInternalBlur = async () => {
    try {
      let isValidCard = false
      console.log('onInternalBlur called')
      const numberWithoutSpace = cardNumber.replace(/[^0-9]/g, '')
      const checkCardNumber = checkValidCardNumber(numberWithoutSpace)
      if (checkCardNumber && numberWithoutSpace.length > 11) {
        if (checkValidCard(cardNumber.replace(/[^0-9]/g, ''), cardVendor ?? '')) {
          isValidCard = true
        } else if (cardNumber.length > 0) {
          throw new Error()
        }
      }
      if (cardNumber && (checkedCardNumber || isValidCard)) {
        if (checkedCardNumber && onCardNumberBlur && cardNumber) {
          if (cardVendor) {
            onCardNumberBlur(cardVendor, cardNumber.replace(/[^0-9]/g, ''))
          } else {
            const [curCardVendor] = getCardVendorAndImage(cardNumber)
            onCardNumberBlur(curCardVendor, cardNumber.replace(/[^0-9]/g, ''))
          }
        }
      } else {
        if (cardNumber.length > 0) {
          throw new Error()
        }
      }
    } catch (error) {
      console.log('catch block')
      const errorMessage = getCardValidatonError(cardNumber.replace(/[^0-9]/g, ''), cardVendor ?? '')
      setErrorMsg(errorMessage)
      isValidCardNumber(false)
    }
  }

  // On change of cardNumber do this
  const internalOnChangeCard = (text: string) => {
    errorMsg && setErrorMsg('')
    const numberWithoutSpace = text.replace(/[^0-9]/g, '')
    let cardLength = 0
    /* paste card number fix */
    const [vendor, curCardImage] = getCardVendorAndImage(numberWithoutSpace)
    if (cardVendor !== vendor) {
      setCardVendor(vendor)
      cardLength = maxCardLength[vendor as CardVendors]
      const cardLengthWithSpaces = cardLength + cardFormatSpacersLength[vendor as CardVendors]
      console.log(cardLengthWithSpaces)
      setDigitLength(cardLength)
      setMaxLength(cardLengthWithSpaces)
      setMaxCvvLength(maxCVVLength[vendor as CardVendors])
      setCardImage(curCardImage)
    }
    const formattedCardNumber = formatCardNumber(
      numberWithoutSpace,
      vendor === 'diners' && numberWithoutSpace.length <= 14 ? 14 : cardLength || digitLength,
    )
    const checkCardNumber = checkValidCardNumber(numberWithoutSpace)
    if (checkCardNumber && numberWithoutSpace.length > 11) {
      !checkedCardNumber && setCheckedCardNumber(true)
      isValidCardNumber(true)
    } else {
      checkedCardNumber && setCheckedCardNumber(false)
      isValidCardNumber(false)
    }
    setCardNumber(formattedCardNumber)
    if (formattedCardNumber.length < previousCardNumber.length && previousCursorPosition < previousCardNumber.length) {
      setCursor(true)
      let newPosition = previousCursorPosition - 1
      if (newPosition > 1 && formattedCardNumber.charAt(newPosition - 1) === ' ') {
        newPosition = newPosition - 1
      }
      setCursorPostion(newPosition)
    } else if (
      formattedCardNumber.length > previousCardNumber.length &&
      previousCursorPosition < previousCardNumber.length
    ) {
      setCursor(true)
      let newPosition = previousCursorPosition + 1
      if (newPosition > 1 && formattedCardNumber.charAt(newPosition - 1) === ' ') {
        newPosition = newPosition + 1
      }
      setCursorPostion(newPosition)
    } else {
      setCursor(false)
    }
    setPreviousCardNumber(formattedCardNumber)
  }

  // When deleting the card number and card vendor is present
  useEffect(() => {
    if (!isEmpty(cardNumber) && cardNumber?.length < 3 && cardVendor) {
      setCardImage(undefined)
      setCardVendor(undefined)
    }
  }, [cardNumber, cardVendor])

  // Check if the cardNumber length is equal to the maxLength
  useEffect(() => {
    if (!isEmpty(cardNumber) && cardNumber.length === maxLength) {
      if (checkValidCardNumber(cardNumber.replace(/[^0-9]/g, ''))) {
        // go to next input only if cardNumber validated
        nextRef?.current?.focus()
      }
    }
  }, [cardNumber, maxLength, nextRef])

  useEffect(() => {
    if (
      !isEmpty(cardNumber) &&
      cardNumber.length === maxLength &&
      !checkValidCardNumber(cardNumber.replace(/[^0-9]/g, ''))
    ) {
      const errorMessage = getCardValidatonError(cardNumber.replace(/[^0-9]/g, ''), cardVendor ?? '')
      setErrorMsg(errorMessage)
      isValidCardNumber(false)
    }
  }, [cardNumber, cardVendor, innerRef, isValidCardNumber, maxLength, setCardNumber])

  // TODO: renderIcon

  const renderIcon = () => {
    if (errorMsg) {
      return <InvalidCardIcon width={30} height={30} />
    } else if (cardImage || cardVendor) {
      return renderCardIcon(cardVendor, cardImage)
    } else {
      return <CardIcon width={30} height={30} color={theme?.brandColor as ColorOptions} />
    }
  }
  console.log('brandColor', theme?.brandColor)
  const onSelectionChange = (event: any) => {
    setCursor(false)
    setPreviousCursorPosition(event.nativeEvent.selection.start)
  }

  return (
    <>
      <Box {...styles.cardContainer}>
        {isMaterialInput && (
          <MaterialTextInput
            autoFocus={false}
            innerRef={innerRef}
            maxLength={maxLength}
            onBlur={onInternalBlur}
            value={cardNumber}
            onChangeText={(text: string) => internalOnChangeCard(text)}
            cursorPosition={cursor ? cursorPosition : -1}
            placeholder={'Card Number'}
            placeholderTextColor={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions}
            keyboardType={'number-pad'}
            color={theme?.bodyTextColor}
            error={errorMsg || errMsg}
            testID={'dcccTextInput'}
            onSelectionChange={onSelectionChange}
            rightComponent={renderIcon()}
            bottomLineColor={theme?.brandColor as ColorOptions}
            tintColor={theme?.brandColor as ColorOptions}
            errorColor={errorColor}
          />
        )}
      </Box>
      {!isMaterialInput && (
        <TextInputNew
          autoFocus={false}
          innerRef={innerRef}
          maxLength={maxLength}
          onBlur={onInternalBlur}
          value={cardNumber}
          onChangeText={(text: string) => internalOnChangeCard(text)}
          cursorPosition={cursor ? cursorPosition : -1}
          placeholder={placeholder ?? 'Card Number'}
          placeholderTextColor={convertHexToRGBA(theme?.bodyTextColor, 40) as ColorOptions}
          fontSize={12}
          keyboardType={'number-pad'}
          color={theme?.bodyTextColor}
          error={errorMsg || errMsg}
          touched={true}
          testID={'dcccTextInput'}
          onSelectionChange={onSelectionChange}
          rightComponent={renderIcon()}
          borderColor={convertHexToRGBA(theme?.brandColor, 40) as ColorOptions}
          focusBorderColor={convertHexToRGBA(theme?.brandColor, 40) as ColorOptions}
          borderRadius={8}
          paddingHorizontal={8}
          paddingVertical={8}
          type={'body10'}
        />
      )}
      {cardNumFooter ? cardNumFooter : undefined}
    </>
  )
}

export { CardNumberInput }
